import type { ResumeRecord } from './onDemandResume';

export const RESUME_MIN_SECONDS = 10;
export const RESUME_END_GUARD_SECONDS = 15;
export const RESUME_REWIND_SECONDS = 5;
export const CHAPTER_SNAP_SECONDS = 30;

export interface ComputeResumeStartArgs {
    /** A URL hash `#t=` / `#<chapter>` is present — it always wins, so resume is skipped. */
    hasHashSeek: boolean;
    /** Server-derived resume position in seconds (already guarded backend-side), or null. */
    serverResumePosition: number | null;
    /**
     * True when the server has confirmed the viewer already finished this on-demand. serverResumePosition
     * is null for that case too (same as "no server session at all"), so this is what lets us tell the
     * two apart — without it, a stale local/anonymous position from before the viewer completed it
     * elsewhere would win by default. Takes precedence over the local fallback even when false would
     * otherwise be eligible.
     */
    serverCompleted: boolean;
    /** The localStorage record for this webinar, or null. */
    local: ResumeRecord | null;
    /** Chapter start timestamps in seconds (the timeline's ChapterChangedEvent timestamps), any order. */
    chapterStarts: number[];
    /** Webinar duration in seconds; 0 when unknown. */
    durationSeconds: number;
}

export interface ResumeStart {
    /** The position to hand the player as its start position, or undefined to start at 0. */
    startPosition: number | undefined;
    /** The caller should mark the local record completed (watched-to-end via a shortened edit). */
    markLocalCompleted: boolean;
}

export function computeResumeStart(args: ComputeResumeStartArgs): ResumeStart {
    const none: ResumeStart = { startPosition: undefined, markLocalCompleted: false };
    if (args.hasHashSeek) return none;

    // The server's word on completion is authoritative — it must win over any local record, not just
    // be skipped by it. A local record can only be MORE stale than the server, never more current: the
    // server position comes from the same session-tracking history the local fallback is a stand-in
    // for, on a device/account the server does have identity for.
    if (args.serverCompleted) {
        return { startPosition: undefined, markLocalCompleted: args.local != null && !args.local.completed };
    }

    const localPos =
        args.local && !args.local.completed && args.local.position >= RESUME_MIN_SECONDS ? args.local.position : null;

    const raw = args.serverResumePosition ?? localPos;
    if (raw == null) return none;

    const chapterStart = args.chapterStarts
        .filter((t) => t <= raw)
        .sort((a, b) => a - b)
        .pop();

    let target: number;
    if (chapterStart !== undefined && raw - chapterStart <= CHAPTER_SNAP_SECONDS) {
        target = chapterStart;
    } else {
        target = Math.max(chapterStart ?? 0, raw - RESUME_REWIND_SECONDS);
    }
    if (target <= 0) return none;

    if (args.durationSeconds > 0 && target >= args.durationSeconds - RESUME_END_GUARD_SECONDS) {
        return { startPosition: undefined, markLocalCompleted: args.local != null && !args.local.completed };
    }

    return { startPosition: target, markLocalCompleted: false };
}
